import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";

// Detector de versión: la PWA instalada no recarga la página al reabrirse
// (reanuda la que tenía en memoria) y el service worker sirve la versión
// guardada, así que no se entera de un deploy nuevo. Cada build publica
// /version.json con su sello y lleva el mismo sello dentro (BUILD_VERSION, de
// craco.config.js); aquí los comparamos al arrancar, al volver a primer plano
// y cada 5 minutos.
// - Al arrancar con una versión vieja: se actualiza sola (aún no hay nada a
//   medias), como mucho una vez cada 10 minutos para no entrar en bucle.
// - Más tarde: aviso con botón «Actualizar».
const CHECK_MS = 5 * 60 * 1000;
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || null;
const AUTO_KEY = "colygon_auto_update_at";
const AUTO_EVERY_MS = 10 * 60 * 1000;

// Activa el service worker nuevo y recarga cuando ya controla la página. Con
// una recarga sin más, el service worker viejo volvía a servir la versión
// guardada y el botón «Actualizar» no actualizaba.
export async function applyUpdate() {
  let done = false;
  const reload = () => {
    if (done) return;
    done = true;
    window.location.reload();
  };
  setTimeout(reload, 8000);
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (!reg) return reload();
    navigator.serviceWorker.addEventListener("controllerchange", reload);
    const activate = (worker) => worker.postMessage({ type: "SKIP_WAITING" });
    if (reg.waiting) return activate(reg.waiting);
    reg.addEventListener("updatefound", () => {
      const worker = reg.installing;
      worker?.addEventListener("statechange", () => {
        if (worker.state === "installed") activate(worker);
      });
    });
    await reg.update();
    if (reg.waiting) activate(reg.waiting);
    else if (!reg.installing) reload();
  } catch {
    reload();
  }
}

const UpdatePrompt = () => {
  const { t } = useI18n();
  const baseline = useRef(BUILD_VERSION);
  const notified = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let first = true;

    const fetchVersion = async () => {
      const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      return data?.v || null;
    };

    const autoUpdateAllowed = () => {
      try {
        const last = Number(localStorage.getItem(AUTO_KEY) || 0);
        if (Date.now() - last < AUTO_EVERY_MS) return false;
        localStorage.setItem(AUTO_KEY, String(Date.now()));
        return true;
      } catch {
        return false;
      }
    };

    const check = async () => {
      const atStart = first;
      first = false;
      try {
        const v = await fetchVersion();
        if (cancelled || !v) return;
        if (baseline.current === null) {
          baseline.current = v;
          return;
        }
        if (v === baseline.current || notified.current) return;
        notified.current = true;
        if (atStart && autoUpdateAllowed()) {
          applyUpdate();
          return;
        }
        toast(t("layout.update_available"), {
          id: "app-update",
          duration: Infinity,
          action: { label: t("layout.update_button"), onClick: applyUpdate },
        });
      } catch {
        // Sin red o sin version.json: silencio — no es asunto del usuario.
      }
    };

    check();
    const interval = setInterval(check, CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default UpdatePrompt;
